/**
 * 月令五行用事引擎
 * 计算每个月支（月建）的人元司令分野
 * 子平真诠分野法：寅月戊七丙七甲十六，卯月甲十乙二十……
 * 展示各藏干在当令月份中的用事天数及当前日主所处阶段
 */

import { type FourPillars, STEM_ELEMENT, type Element } from '../../baziCalc';
import { getSolarTermDate, toJulianDay } from './solarTermEngine';

// ── 十二地支人元司令分野数据（子平真诠）──────────────────────
// 顺序：[藏干, 五行, 天数]
export interface FenYeItem {
  stem: string;        // 藏干天干
  element: Element;    // 所属五行
  days: number;        // 用事天数
  color: string;       // 五行色
}

const MONTH_FENYE: Record<string, FenYeItem[]> = {
  '寅': [
    { stem: '戊', element: '土', days: 7, color: '#A9744F' },
    { stem: '丙', element: '火', days: 7, color: '#D14B45' },
    { stem: '甲', element: '木', days: 16, color: '#7BC67E' },
  ],
  '卯': [
    { stem: '甲', element: '木', days: 10, color: '#7BC67E' },
    { stem: '乙', element: '木', days: 20, color: '#7BC67E' },
  ],
  '辰': [
    { stem: '乙', element: '木', days: 9, color: '#7BC67E' },
    { stem: '癸', element: '水', days: 3, color: '#4A90D9' },
    { stem: '戊', element: '土', days: 18, color: '#A9744F' },
  ],
  '巳': [
    { stem: '戊', element: '土', days: 5, color: '#A9744F' },
    { stem: '庚', element: '金', days: 9, color: '#D4AF37' },
    { stem: '丙', element: '火', days: 16, color: '#D14B45' },
  ],
  '午': [
    { stem: '丙', element: '火', days: 10, color: '#D14B45' },
    { stem: '己', element: '土', days: 9, color: '#A9744F' },
    { stem: '丁', element: '火', days: 11, color: '#D14B45' },
  ],
  '未': [
    { stem: '丁', element: '火', days: 9, color: '#D14B45' },
    { stem: '乙', element: '木', days: 3, color: '#7BC67E' },
    { stem: '己', element: '土', days: 18, color: '#A9744F' },
  ],
  '申': [
    { stem: '己', element: '土', days: 7, color: '#A9744F' },
    { stem: '戊', element: '土', days: 3, color: '#A9744F' },
    { stem: '壬', element: '水', days: 3, color: '#4A90D9' },
    { stem: '庚', element: '金', days: 17, color: '#D4AF37' },
  ],
  '酉': [
    { stem: '庚', element: '金', days: 10, color: '#D4AF37' },
    { stem: '辛', element: '金', days: 20, color: '#D4AF37' },
  ],
  '戌': [
    { stem: '辛', element: '金', days: 9, color: '#C0C0E0' },
    { stem: '丁', element: '火', days: 3, color: '#D14B45' },
    { stem: '戊', element: '土', days: 18, color: '#A9744F' },
  ],
  '亥': [
    { stem: '戊', element: '土', days: 7, color: '#A9744F' },
    { stem: '甲', element: '木', days: 5, color: '#7BC67E' },
    { stem: '壬', element: '水', days: 18, color: '#4A90D9' },
  ],
  '子': [
    { stem: '壬', element: '水', days: 10, color: '#4A90D9' },
    { stem: '癸', element: '水', days: 20, color: '#4A90D9' },
  ],
  '丑': [
    { stem: '癸', element: '水', days: 9, color: '#4A90D9' },
    { stem: '辛', element: '金', days: 3, color: '#D4AF37' },
    { stem: '己', element: '土', days: 18, color: '#A9744F' },
  ],
};

// 月支 → solarTermEngine 节气索引（0=立春/寅, 1=惊蛰/卯, ..., 11=小寒/丑）
const BRANCH_TO_TERM_IDX: Record<string, number> = {
  '寅': 0, '卯': 1, '辰': 2, '巳': 3, '午': 4, '未': 5,
  '申': 6, '酉': 7, '戌': 8, '亥': 9, '子': 10, '丑': 11,
};

// 月支中文名
const BRANCH_NAMES: Record<string, string> = {
  '寅': '寅月', '卯': '卯月', '辰': '辰月', '巳': '巳月',
  '午': '午月', '未': '未月', '申': '申月', '酉': '酉月',
  '戌': '戌月', '亥': '亥月', '子': '子月', '丑': '丑月',
};

// 节气名称（与 solarTermEngine 的 TERM_NAMES 对应）
const JIEQI_NAMES = [
  '立春', '惊蛰', '清明', '立夏', '芒种', '小暑',
  '立秋', '白露', '寒露', '立冬', '大雪', '小寒',
];

// ── 各月支藏干用事详解文案（多语言）─────────────────────────

export interface YuelingDetail {
  title: string;          // 月令总述
  personality: string;      // 性格影响
  career: string;           // 事业影响
  emotion: string;          // 感情影响
  health: string;           // 健康影响
  advice: string;           // 行事建议
}

const YUELING_DETAILS: Record<string, YuelingDetail> = {
  '寅': {
    title: '寅月春初，木气渐旺，土火相参，甲木司令。此时阳气初生，万物复苏，命局如嫩苗破土，生机暗藏而待发。',
    personality: '寅月出生之人，受甲木余气与戊土中气影响，性格中兼有开拓之力与务实之基。丙火长生于寅，赋予热情与创造力，但初春木气未极，行事多需耐心培育，不可操之过急。',
    career: '寅月用事阶段不同，所宜行业各异。戊土用事之初（前7日），适合基础建设、团队搭建；丙火用事中期（次7日），适合创意策划、市场开拓；甲木司令后期（后16日），适合领导决策、项目启动。',
    emotion: '寅月感情如春日萌芽，丙火带来温暖吸引力，甲木赋予独立个性。戊土用事期感情较为务实稳重，转入丙火后热情渐增，甲木司令期则强调精神共鸣与自主空间。',
    health: '寅月木旺克土，需注意脾胃调理。丙火暗藏，防肝胆虚火上升。春季多风，宜注意呼吸系统保养，多食甘味以养脾，适度运动以疏肝。',
    advice: '寅月行事宜循序渐进，先稳后动。戊土日宜规划布局，丙火日宜展示推广，甲木日宜大胆决策。顺势而为，事半功倍。',
  },
  '卯': {
    title: '卯月仲春，木气极盛，甲乙并进，仁德当令。此时百花争艳，命局如翠竹临风，柔韧而富有弹性。',
    personality: '卯月出生之人，甲乙木并旺，性格温和仁厚，善于沟通协调。乙木主事则更具细腻与耐心，甲木辅之则有进取之志。整体而言，重情义、善变通，但需注意优柔寡断之倾向。',
    career: '卯月木气专旺，宜从事教育、文化、设计、园艺、心理咨询等木气相关行业。甲木用事期（前10日）适合开拓新领域，乙木司令期（后20日）则宜深耕细作、精益求精。',
    emotion: '卯月感情丰富细腻，甲木带来主动追求的勇气，乙木赋予温柔体贴的魅力。感情中重视精神交流与共同成长，宜找互补型伴侣以平衡过旺木气。',
    health: '卯月木气过旺，需防肝气郁结、情绪波动。建议多食酸味之外搭配甘味，以土泄木之过。保持规律作息，避免熬夜伤肝。',
    advice: '卯月宜顺势而为，木旺之时广结善缘、拓展人脉。甲木日宜主动出击，乙木日宜守成精进。切忌逆势而为，强行从事金性行业。',
  },
  '辰': {
    title: '辰月季春，土气渐旺，水木余气暗藏，戊土司令。此时春末夏初，命局如湿土育秧，包容而蓄藏。',
    personality: '辰月出生之人，戊土当令赋予踏实稳重的底色，乙木余气带来柔韧变通，癸水藏根增添智慧与洞察力。性格包容大度，善于整合资源，但有时过于保守，缺乏锐气。',
    career: '辰月土旺，适合房地产、金融、农业、行政管理等土性行业。乙木用事期（前9日）可从事创意与策划，癸水期（次3日）适合财务分析，戊土司令期（后18日）宜于管理与执行。',
    emotion: '辰月感情务实而含蓄，戊土用事期追求稳定与安全感，乙木期则增添浪漫气息。感情中重视物质基础与家庭建设，是可靠的长期伴侣型。',
    health: '辰月湿气偏重，需注意脾胃运化与湿邪困阻。土旺克水，防肾脏与泌尿系统负担。饮食宜清淡利湿，多食薏米、红豆等祛湿食材。',
    advice: '辰月宜守不宜攻，以稳为主。乙木日可适度创新，戊土日宜深耕主业。土旺之月适合积累资源、完善体系，为夏季发展蓄力。',
  },
  '巳': {
    title: '巳月初夏，火气渐炽，金土相参，丙火司令。此时阳气日盛，命局如炉火初红，热情而内敛。',
    personality: '巳月出生之人，丙火当令赋予热情与表达力，戊土中气带来务实与稳重，庚金初生增添决断与原则。性格外向开朗，善于交际表达，但有时急躁冲动，需培养耐心。',
    career: '巳月火旺，适合演艺、营销、教育、餐饮、能源等火性行业。戊土用事期（前5日）适合团队建设，庚金期（次9日）适合制定规则，丙火司令期（后16日）宜于展示与领导。',
    emotion: '巳月感情热烈直接，丙火带来激情与浪漫，庚金赋予原则与承诺。戊土期感情较为理性，转入丙火后情感升温迅速。需注意避免过热伤感情，保持适度空间。',
    health: '巳月火旺克金，需注意心肺与呼吸系统。丙火炽盛，防上火、失眠与心血管负担。饮食宜清凉，多食苦瓜、莲子等清心食物，避免辛辣刺激。',
    advice: '巳月宜热情投入但忌过度消耗。戊土日宜稳扎稳打，庚金日宜订立规矩，丙火日宜大展拳脚。火旺之时，注意劳逸结合。',
  },
  '午': {
    title: '午月仲夏，火气极盛，己土丁炎，烈日当空。此时阳极阴生，命局如正午骄阳，光芒万丈而暗藏收敛之机。',
    personality: '午月出生之人，丙丁并旺，性格热情奔放，充满感染力与领导魅力。己土中气赋予务实与包容，使烈火的热情不失稳重。天生具有号召力，但需防止急躁与情绪化。',
    career: '午月火气鼎盛，宜从事演艺、公关、管理、能源、餐饮等需要热情与影响力的行业。丙火用事期（前10日）适合领导与展示，己土期（次9日）适合管理与协调，丁火司令期（后11日）适合深耕与维系。',
    emotion: '午月感情炽热浓烈，丙丁并旺带来强烈的吸引力与占有欲。感情中全心投入，但需要学会留白与包容。己土用事期感情趋于平稳，丁火期则更加专注执着。',
    health: '午月心火亢盛，需重点养护心脑血管。防中暑、失眠、情绪过激。饮食以清淡为主，多食绿豆汤、苦瓜、百合等清心降火之物，保持充足睡眠。',
    advice: '午月阳极之时，宜顺势张扬而非强行收敛。丙火日宜主动出击，己土日宜巩固成果，丁火日宜专注深耕。火旺月切忌钻牛角尖，遇事放宽心。',
  },
  '未': {
    title: '未月季夏，土气当令，木火余气犹存，己土司令。此时暑热未退，命局如田园盛夏，丰饶而需调理。',
    personality: '未月出生之人，己土当令赋予包容与承载力，丁火余气保留热情与创造力，乙木余根增添柔韧与进取。性格宽厚仁慈，善于照顾他人，但需防止过度付出而耗损自身。',
    career: '未月土旺，适合农业、食品、房地产、教育、医疗等土性行业。丁火用事期（前9日）可从事创意与宣传，乙木期（次3日）适合开拓与策划，己土司令期（后18日）宜于管理与执行。',
    emotion: '未月感情温暖而包容，己土赋予稳定与安全感，丁火带来浪漫与温情。感情中善于付出与照顾，是理想的伴侣型。乙木期感情增添新鲜感，己土期则趋于深沉持久。',
    health: '未月湿热交蒸，脾胃负担较重。需防消化不良、湿疹与暑湿困脾。饮食宜清淡利湿，多食瓜果蔬菜，避免油腻甜腻之物，保持通风凉爽。',
    advice: '未月土旺泄火，宜借势而为而非逆势而动。丁火日宜展示才华，乙木日宜拓展新局，己土日宜守成固本。湿热之月，身心均需清爽。',
  },
  '申': {
    title: '申月初秋，金气渐旺，土气相参，庚金司令。此时阳气渐收，命局如秋水初凉，清明而锐利。',
    personality: '申月出生之人，庚金当令赋予果断与纪律性，戊土余气保留务实与稳重，己土初生增添细腻与包容。性格刚毅而理性，善于分析与决策，但需防止过于严苛与冷漠。',
    career: '申月金旺，适合金融、法律、工程、科技、军警等金性行业。己土用事期（前7日）适合协调与规划，戊土期（次3日）适合执行与落实，壬水期（再3日）适合分析与变通，庚金司令期（后17日）宜于决策与攻坚。',
    emotion: '申月感情理性而深沉，庚金赋予原则与承诺，土气增添稳定与包容。感情中不善甜言蜜语，但行动可靠。需注意金气过旺可能带来的情感疏离，适当增加温情互动。',
    health: '申月金旺克木，需注意肝胆与筋骨。庚金肃杀，防呼吸系统与皮肤敏感。秋季干燥，宜润肺养阴，多食银耳、百合、梨等滋润之品，避免辛辣。',
    advice: '申月金气渐盛，宜顺势收敛而非强行扩张。己土日宜协调关系，戊土日宜夯实基础，庚金日宜果断决策。秋凉之月，宜养收之道。',
  },
  '酉': {
    title: '酉月仲秋，金气极盛，辛庚并进，肃杀当令。此时秋意正浓，命局如霜刃出鞘，精准而凛冽。',
    personality: '酉月出生之人，庚辛并旺，性格刚毅果决，极具原则性与执行力。思维清晰缜密，追求完美与效率。金气纯粹，为人正直可靠，但需注意过于严苛、不善变通之倾向。',
    career: '酉月金气鼎盛，宜从事金融、法律、审计、医疗外科、精密制造、军警等需要精准与决断的行业。庚金用事期（前10日）适合制定规则与战略，辛金司令期（后20日）宜于执行与打磨。',
    emotion: '酉月感情专一而深沉，金性赋予忠诚与承诺，但表达方式偏于含蓄内敛。感情中重视原则与尊重，需学会柔软表达，避免因过于理性而让对方感到冷淡。',
    health: '酉月金旺克木，肝胆与筋骨需重点关注。肺气偏旺，注意呼吸道保养与皮肤润泽。秋季干燥，多食滋润之物，保持情绪平稳以防金气过肃。',
    advice: '酉月金旺之时，宜精不宜泛。庚金日宜定战略，辛金日宜抠细节。金气纯粹之月，适合整顿、清理、规范。注意保持人际温度，勿以刚金伤人。',
  },
  '戌': {
    title: '戌月季秋，土气当令，金火余气暗藏，戊土司令。此时秋末冬初，命局如黄土覆霜，厚重而内含变革。',
    personality: '戌月出生之人，戊土当令赋予踏实与包容，辛金余气保留锐利与原则，丁火藏根增添热情与创造力。性格稳重而内藏锋芒，善于在稳定中寻求突破，但需防止固执与压抑。',
    career: '戌月土旺，适合房地产、金融、农业、行政管理等土性行业。辛金用事期（前9日）适合分析与审计，丁火期（次3日）适合创意与突破，戊土司令期（后18日）宜于管理与执行。',
    emotion: '戌月感情深沉而务实，戊土赋予稳定与安全感，辛金带来原则与底线，丁火藏情则内心炽热。感情中不善表达但极度忠诚，需要伴侣懂得其沉默背后的深情。',
    health: '戌月燥土当令，需注意肺燥与皮肤干燥。土旺克水，防肾脏负担。秋季末段，宜润燥养阴，多食滋润之物，保持充足水分摄入，避免过度劳累。',
    advice: '戌月土旺收金，宜稳固基础而非冒进。辛金日宜审视回顾，丁火日宜适度创新，戊土日宜守成固本。秋冬之交，注意保暖润燥，身心均需安稳过渡。',
  },
  '亥': {
    title: '亥月初冬，水气渐旺，木土相参，壬水司令。此时阳气潜藏，命局如寒潭深邃，智慧而内敛。',
    personality: '亥月出生之人，壬水当令赋予深邃智慧与敏锐直觉，甲木余气保留进取与创造力，戊土初生增添稳重与务实。性格内敛深沉，思维灵活，善于洞察先机，但需防止过于多疑与消极。',
    career: '亥月水旺，适合哲学、研究、咨询、信息技术、外交、物流等水性行业。戊土用事期（前7日）适合规划与布局，甲木期（次5日）适合开拓与创新，壬水司令期（后18日）宜于分析与决策。',
    emotion: '亥月感情深沉而细腻，壬水赋予敏感与洞察力，甲木带来主动追求的意愿。感情中重视精神共鸣，善于体察伴侣情绪，但水旺之人易多愁善感，需培养乐观心态。',
    health: '亥月水旺克火，需注意心脑血管与肾脏保暖。冬季初寒，防寒湿侵体。饮食宜温热，多食姜、红枣、羊肉等温阳之物，注意腰部与下肢保暖。',
    advice: '亥月水气渐盛，宜藏不宜露。戊土日宜暗中布局，甲木日宜适度行动，壬水日宜深入分析。冬初之月，养藏为先，蓄力待发。',
  },
  '子': {
    title: '子月仲冬，水气极盛，壬癸并进，寒水当令。此时阴极阳生，命局如深井寒泉，清冷而蕴含生机。',
    personality: '子月出生之人，壬癸并旺，性格聪慧冷静，极具洞察力与适应力。思维如水般灵活，处事圆融变通。水气纯粹赋予深度思考能力，但需防止过于感性、优柔寡断之倾向。',
    career: '子月水气鼎盛，宜从事研究、咨询、信息技术、贸易、外交、艺术等水性行业。壬水用事期（前10日）适合战略规划与宏观分析，癸水司令期（后20日）宜于执行细节与深度钻研。',
    emotion: '子月感情深沉而细腻，壬癸并旺带来丰富的情感世界与敏锐的直觉。感情中重视心灵契合，善于营造浪漫氛围，但水旺之人情绪起伏较大，需学会情绪管理。',
    health: '子月水旺克火，心脑血管与肾脏需重点养护。寒冬之际，防寒保暖至关重要。饮食宜温热，多食黑色食物（黑芝麻、黑豆）补肾，避免生冷，坚持适度运动以升阳。',
    advice: '子月阴极之时，宜静不宜动。壬水日宜高瞻远瞩，癸水日宜精益求精。水旺之月适合学习、反思、储备。冬至前后，一阳初生，暗中蓄力，以待春来。',
  },
  '丑': {
    title: '丑月季冬，土气当令，水金余气暗藏，己土司令。此时寒冬腊月，命局如冻土藏金，厚重而蓄藏。',
    personality: '丑月出生之人，己土当令赋予踏实与包容，癸水余气保留智慧与敏感，辛金藏根增添原则与精细。性格稳重内敛，善于忍耐与坚持，但需防止过于保守与沉闷。',
    career: '丑月土旺，适合金融、房地产、农业、传统手工艺、行政管理等土性行业。癸水用事期（前9日）适合分析与策划，辛金期（次3日）适合精细打磨，己土司令期（后18日）宜于管理与执行。',
    emotion: '丑月感情深沉而持久，己土赋予稳定与忠诚，癸水带来细腻与体贴。感情中不善甜言蜜语但行动踏实，是经得起时间考验的伴侣。需注意冬季沉闷气氛，适当增加生活情趣。',
    health: '丑月寒湿交侵，脾胃与肾脏需双重养护。土旺克水，需注意湿气困脾与寒湿侵肾。饮食宜温热易消化，多食山药、红枣、姜汤等温补之品，避免生冷油腻。',
    advice: '丑月寒冬之际，宜藏不宜露。癸水日宜暗中观察，辛金日宜精益求精，己土日宜巩固基础。岁末之月，适合总结回顾，为新一年蓄力。注意防寒保暖，身心均需温养。',
  },
};

// ── 对外接口 ─────────────────────────────────────────────────

export interface YuelingResult {
  branch: string;           // 月支
  branchName: string;       // 月支名称（如"午月"）
  totalDays: number;        // 分野总天数
  items: Array<FenYeItem & { isActive: boolean; startDay: number; endDay: number; percent: number }>;
  activeIndex: number;      // 当前用事藏干索引
  activeStem: string;       // 当前用事藏干
  activeElement: Element;   // 当前用事五行
  activeDesc: string;       // 当前用事描述（如"丙火用事"）
  detail: YuelingDetail;    // 当月详解
  startTermName: string;    // 起始节气名
  activeDayNumber: number;  // 当前所在用事天数（第N天）
  qiPhase: '节' | '中气';   // 当前所处阶段：节（前半月）| 中气（后半月）
}

/**
 * 计算月令五行用事数据
 * @param fp 四柱数据
 * @param birthTime 出生时间字符串（如"1990-06-15 12:00"）
 */
export function calcYueling(fp: FourPillars, birthTime: string): YuelingResult | null {
  try {
    const monthBranch = fp.month.branch;
    const fenye = MONTH_FENYE[monthBranch];
    if (!fenye) return null;

    // 计算距当月节气的天数（接入精确节气引擎）
    const [datePart, timePart = '00:00'] = birthTime.split(' ');
    const [y, bm, bd] = datePart.split('-').map(Number);
    const [bh, bmn = 0] = timePart.split(':').map(Number);

    // 根据月支找到对应的节气索引
    const termIdx = BRANCH_TO_TERM_IDX[monthBranch];
    if (termIdx === undefined) return null;

    // 小寒（丑月）在次年 1 月，其余节气在当年
    // 但要注意：如果出生日在 1 月，小寒应取当年的小寒（即 y 年 1 月）
    // solarTermEngine 的 getSolarTermDate(year, 11) 返回的是 year+1 年 1 月的小寒
    // 所以对丑月：如果出生在 1 月，应用 y-1 年的小寒；如果在 12 月，应用 y 年的小寒
    let termYear = y;
    if (termIdx === 11 && bm !== 1) {
      // 小寒在次年 1 月，但出生在当年 12 月，应取 y 年的小寒（即 getSolarTermDate(y, 11) 返回 y+1 年 1 月）
      termYear = y;
    } else if (termIdx === 11 && bm === 1) {
      // 出生在 1 月，小寒应取上一年的（getSolarTermDate(y-1, 11) 返回 y 年 1 月）
      termYear = y - 1;
    }

    // 获取精确节气时刻（北京时 UTC+8）
    const termDate = getSolarTermDate(termYear, termIdx);
    const termJDN = toJulianDay(
      termDate.year, termDate.month, termDate.day,
      termDate.hours, termDate.minutes, termDate.seconds,
    );
    const birthJDN = toJulianDay(y, bm, bd, bh, bmn, 0);

    // 距节气起始的天数（1-based）
    const diffDaysRaw = birthJDN - termJDN;
    const diffDays = Math.max(0, Math.floor(diffDaysRaw)) + 1;

    // 构建分野项
    let cumulative = 0;
    const items = fenye.map((fy) => {
      const startDay = cumulative + 1;
      const endDay = cumulative + fy.days;
      cumulative += fy.days;
      const isActive = diffDays >= startDay && diffDays <= endDay;
      return {
        ...fy,
        isActive,
        startDay,
        endDay,
        percent: Math.round((fy.days / cumulative) * 100), // 先占位，后面修正
      };
    });

    // 修正 percent（基于 totalDays）
    const totalDays = cumulative;
    const correctedItems = items.map(it => ({
      ...it,
      percent: Math.round((it.days / totalDays) * 100),
    }));

    const foundActiveIndex = correctedItems.findIndex(it => it.isActive);
    const activeIndex = foundActiveIndex >= 0 ? foundActiveIndex : correctedItems.length - 1;
    const activeItem = correctedItems[activeIndex] || correctedItems[0];

    // 计算当前在该藏干用事段的第几天
    const activeDayNumber = activeItem
      ? Math.max(1, Math.min(activeItem.days, diffDays - activeItem.startDay + 1))
      : 1;

    // 判断节/中气：前半月（diffDays <= 15）为节，后半月为中气
    const qiPhase: '节' | '中气' = diffDays <= 15 ? '节' : '中气';

    return {
      branch: monthBranch,
      branchName: BRANCH_NAMES[monthBranch] || `${monthBranch}月`,
      totalDays,
      items: correctedItems,
      activeIndex,
      activeStem: activeItem.stem,
      activeElement: activeItem.element,
      activeDesc: `${activeItem.stem}${activeItem.element}用事`,
      detail: YUELING_DETAILS[monthBranch] || YUELING_DETAILS['寅'],
      startTermName: JIEQI_NAMES[termIdx] ?? '',
      activeDayNumber,
      qiPhase,
    };
  } catch {
    return null;
  }
}

/** 导出节气人元司令分野数据（供弹窗进度条使用） */
export const JIE_QI_FENYE = MONTH_FENYE;

/**
 * 获取月支藏干详细文案（用于详情展开）
 */
export function getYuelingDetail(branch: string): YuelingDetail {
  return YUELING_DETAILS[branch] || YUELING_DETAILS['寅'];
}
